// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Calendar from "@insite/mobius/Icons/Calendar";
import Truck from "@insite/mobius/Icons/Truck";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import constants from "@store/constants";
import { hasMeaningfulMinimumOrderQuantity } from "@store/Framework/Common/Utilities/productLineUtilities";
import useSupportPageLink from "@store/Framework/Common/Hooks/useSupportPageLink";
import resolveZeroPriceDisplay, { zeroPriceMaskValue } from "@store/Framework/Common/Utilities/resolveZeroPriceDisplay";
import { getProductDetailMultiLocationCatalogInfo } from "@store/Framework/Store/Greif/Pages/ProductDetails/ProductDetailsSelectors";
import {
    FullTruckLoadEntryModel,
    FullTruckLoadModel,
    LeadTimeEntryModel,
    LeadTimeModel,
    MLCAvailableAtOtherShipTosModel,
} from "@store/Framework/Types/ApiModels";
import ProductFullTruckLoad from "@store/Library/Components/ProductFullTruckLoad";
import ProductLeadTime from "@store/Library/Components/ProductLeadTime";
import ProductLineMinimumOrderQuantity from "@store/Library/Components/ProductLine/ProductLineMinimumOrderQuantity";
import ZeroPriceContactUsAction, {
    zeroPriceContactUsActionStyles,
} from "@store/Library/Components/ZeroPriceContactUsAction";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Tooltip from "@store/MobiusOverrides/Tooltip/Tooltip";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

type TableData = {
    shipToId?: string;
    customerName?: string;
    customerItemNumber?: string | null;
    plant?: string | null;
    fullTruckLoad?: FullTruckLoadModel;
    leadTime?: LeadTimeModel;
    pricing?: number;
    priceDisplay?: string;
    warehouseDescription?: string;
    currency?: string;
};

type ModalContent = {
    heading: string;
    content: string;
};

const defaultModalContent: ModalContent = { heading: "", content: "" };

const enum fields {
    isMlcDisplay = "isMlcDisplay",
}

export interface PreviouslyPurchasedByTableProps extends WidgetProps {
    fields: {
        [fields.isMlcDisplay]: boolean;
    };
}

export interface PreviouslyPurchasedByTableStyles {
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    shipToCell?: DataTableHeaderProps;
    headerIcon?: IconPresentationProps;
    headerModal?: ModalPresentationProps;
    availabilityWrapper?: InjectableCss;
    availabilityLabel?: TypographyPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    shipToModalContinueButton?: ButtonPresentationProps;
    shipToModalCancelButton?: ButtonPresentationProps;
    shipToModalTitle?: TypographyPresentationProps;
    shipToModalText?: TypographyPresentationProps;
    shipToModalFormActions?: FormActionsProps;
    shipToModal?: ModalPresentationProps;
}

const previouslyPurchasedByTableStyles: PreviouslyPurchasedByTableStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                ${x => x.theme.accordion.sectionDefaultProps?.panelProps?.css}
                padding-right: 0;
                padding-left: 0;
            `,
        },
    },
    tableWrapper: {
        css: css`
            width: 100%;
            max-height: ${pxToRem(475)};
            overflow: auto;
        `,
    },
    dataTable: {
        cssOverrides: {
            cell: css`
                ${x => x.theme.dataTable.defaultProps?.cssOverrides?.cell}
                white-space: wrap;
            `,
        },
    },
    shipToCell: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    headerIcon: {
        size: 16,
        color: "inherit",
    },
    availabilityWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(8))}
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    availabilityLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    shipToModal: {
        sizeVariant: "small",
    },
    shipToModalTitle: {
        variant: "p",
        css: css`
            text-align: center;
            margin-bottom: ${pxToRem(24)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            font-size: ${pxToRem(20)};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    shipToModalText: {
        variant: "p",
        css: css`
            text-align: center;
            font-weight: ${x => x.theme.design.typography.weight.base};
            font-size: ${x => x.theme.design.typography.size.base};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    shipToModalFormActions: {
        spacing: pxToRem(24),
        extendedStyles: {
            flexWrapper: {
                content: {
                    css: css`
                        justify-content: center;
                    `,
                },
            },
        },
    },
    shipToModalCancelButton: { variant: "tertiary" },
};

const prepareTableData = (
    fullTruckLoads: FullTruckLoadEntryModel[] = [],
    leadTimes: LeadTimeEntryModel[] = [],
    realTimePricingData: ProductPriceDto[] = [],
    availableAtOtherShipTos: MLCAvailableAtOtherShipTosModel = {},
    productNumber?: string,
): TableData[] => {
    const ftlsByPlant = fullTruckLoads.reduce((map, f) => {
        if (f?.Plant && !map.has(f.Plant)) {
            map.set(f.Plant, {
                plant: f.Plant,
                ftlQuantity: f.FullTruckLoad,
            });
        }
        return map;
    }, new Map<string, FullTruckLoadModel>());

    const leadTimesByPlant = leadTimes.reduce((map, l) => {
        if (l?.Plant && !map.has(l.Plant)) {
            map.set(l.Plant, {
                isSpecialItem: l.IsSpecialItem,
                leadTimeDays: l.LeadTime,
                minimumOrderQuantity: l.MinimumOrderQuantity,
                plant: l.Plant,
            });
        }
        return map;
    }, new Map<string, LeadTimeModel>());

    const pricingByShipTo = realTimePricingData.reduce((map, p) => {
        const shipTo = p?.additionalResults?.shipToId;
        if (shipTo) {
            const arr = map.get(shipTo) || [];
            arr.push(p);
            map.set(shipTo, arr);
        }
        return map;
    }, new Map<string, ProductPriceDto[]>());

    const result: TableData[] = [];

    for (const [shipToId, entry] of Object.entries(availableAtOtherShipTos)) {
        const pricingEntry = pricingByShipTo.get(shipToId)?.[0];
        const plant = pricingEntry?.additionalResults?.warehouseName;

        result.push({
            shipToId,
            customerName: entry?.customerName,
            customerItemNumber: entry?.customerProductName || productNumber,
            plant,
            fullTruckLoad: plant ? ftlsByPlant.get(plant) : undefined,
            leadTime: plant ? leadTimesByPlant.get(plant) : undefined,
            pricing: pricingEntry?.extendedUnitNetPrice,
            priceDisplay: pricingEntry
                ? `(${pricingEntry.additionalResults?.currency}/${pricingEntry.unitOfMeasure})`
                : undefined,
            warehouseDescription: pricingEntry?.additionalResults?.warehouseDescription,
            currency: pricingEntry?.additionalResults?.currency,
        });
    }

    return result;
};

const PreviouslyPurchasedByTable: FC<PreviouslyPurchasedByTableProps> = ({ fields: { isMlcDisplay } }) => {
    const dispatch = useAppDispatch();
    const { product } = useContext(ProductContext);
    const { fulfillmentMethod, pickUpWarehouse, billToId } = useAppSelector(getSession);
    const isMobile = useIsBreakpoint("sm", "down");
    const [isAccordionExpanded, setIsAccordionExpanded] = useState<boolean | undefined>();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [changeShipModal, setChangeShipModal] = useState(false);
    const [selectedShipTo, setSelectedShipTo] = useState<string | undefined>("");
    const [modalContent, setModalContent] = useState(defaultModalContent);
    const supportPageLink = useSupportPageLink("pricing", product?.id);

    const { isMlcProduct, fullTruckLoads, leadTimes, pricing, availableAtOtherShipTos } =
        useAppSelector(getProductDetailMultiLocationCatalogInfo) || {};

    const hasOtherShipTo = Object.keys(availableAtOtherShipTos || {}).length > 0;

    const styles = useMemo(() => {
        if (!isMlcDisplay) {
            return previouslyPurchasedByTableStyles;
        }

        return mergeProps<PreviouslyPurchasedByTableStyles>(previouslyPurchasedByTableStyles, {
            accordionSection: {
                panelProps: {
                    css: css`
                        padding-bottom: 0;
                    `,
                },
            },
        });
    }, [isMlcDisplay]);

    useEffect(() => {
        if (typeof isAccordionExpanded === "undefined") {
            setIsAccordionExpanded(isMlcProduct);
        }
    }, [isMlcProduct]);

    const tableData = useMemo(() => {
        return prepareTableData(fullTruckLoads, leadTimes, pricing, availableAtOtherShipTos, product?.productNumber);
    }, [fullTruckLoads, leadTimes, pricing, availableAtOtherShipTos, product?.productNumber]);

    if (
        !hasOtherShipTo ||
        (!isMlcDisplay && isMlcProduct && !isMobile) ||
        (isMlcDisplay && (!isMlcProduct || isMobile))
    ) {
        return null;
    }

    const handleChangeClicked = () => {
        if (!selectedShipTo) {
            return;
        }

        sessionStorage.setItem(constants.storage.shipToHasChanged, "true");

        dispatch(
            changeCustomerContext({
                billToId,
                shipToId: selectedShipTo,
                fulfillmentMethod,
                pickUpWarehouse: fulfillmentMethod === FulfillmentMethod.PickUp ? pickUpWarehouse : null,
                returnUrl: window.location.pathname + window.location.search,
                isDefault: false,
            }),
        );

        setChangeShipModal(false);
    };

    const renderPricingCell = (row: TableData) => {
        const zeroPriceDecision = resolveZeroPriceDisplay({
            context: "mlcRow",
            price: row.pricing,
            supportUrl: supportPageLink,
        });

        if (!zeroPriceDecision.showDash) {
            return typeof row.pricing === "number" ? `${row.pricing} ${row.priceDisplay || ""}`.trim() : "";
        }

        const zeroPriceDisplay = row.currency ? `${row.currency} ${zeroPriceMaskValue}` : zeroPriceMaskValue;

        return (
            <StyledWrapper {...zeroPriceContactUsActionStyles.outerWrapper}>
                <Typography>{zeroPriceDisplay}</Typography>
                <ZeroPriceContactUsAction
                    decision={zeroPriceDecision}
                    renderTooltip={tooltipText => <Tooltip text={tooltipText} />}
                />
            </StyledWrapper>
        );
    };

    return (
        <Accordion headingLevel={2}>
            <AccordionSection
                {...styles.accordionSection}
                title={translate("Previously Purchased By Table")}
                onTogglePanel={expanded => setIsAccordionExpanded(!expanded)}
                expanded={isAccordionExpanded}
            >
                <StyledWrapper data-test-selector="previouslyPurchasedByTable" {...styles.tableWrapper}>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader />
                            <DataTableHeader>{translate("Ship To")}</DataTableHeader>
                            <DataTableHeader>{translate("Ships From")}</DataTableHeader>
                            <DataTableHeader>{translate("Customer #")}</DataTableHeader>
                            <DataTableHeader>
                                <StyledWrapper {...styles.availabilityWrapper}>
                                    <Icon src={Calendar} {...styles.headerIcon} />
                                    <Typography
                                        {...styles.availabilityLabel}
                                        title={siteMessage("Product_LeadTimeTooltipRegularProduct") as string}
                                    >
                                        {translate("Lead Time")}
                                    </Typography>
                                    <Clickable
                                        onClick={() => {
                                            setModalIsOpen(true);
                                            setModalContent({
                                                heading: translate("Lead Time"),
                                                content: siteMessage("Product_LeadTimeTooltipRegularProduct") as string,
                                            });
                                        }}
                                    >
                                        <Icon src={AlertCircle} {...styles.headerIcon} />
                                    </Clickable>
                                </StyledWrapper>
                                <StyledWrapper {...styles.availabilityWrapper}>
                                    <Icon src={Truck} {...styles.headerIcon} />
                                    <Typography {...styles.availabilityLabel}>
                                        {translate("Full Truck Load")}
                                    </Typography>
                                    <Clickable
                                        onClick={() => {
                                            setModalIsOpen(true);
                                            setModalContent({
                                                heading: translate("Full Truck Load"),
                                                content: siteMessage("ProductInfo_FtlMessage") as string,
                                            });
                                        }}
                                    >
                                        <Icon src={AlertCircle} {...styles.headerIcon} />
                                    </Clickable>
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader>{translate("Pricing")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {tableData.map(row => (
                                <DataTableRow key={row.shipToId}>
                                    <DataTableCell>
                                        <Link
                                            onClick={() => {
                                                setSelectedShipTo(row.shipToId);
                                                setChangeShipModal(true);
                                            }}
                                        >
                                            {translate("Change Ship To")}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.shipToCell}>{row.customerName}</DataTableCell>
                                    <DataTableCell>{row.warehouseDescription}</DataTableCell>
                                    <DataTableCell>{row.customerItemNumber}</DataTableCell>
                                    <DataTableCell>
                                        <StyledWrapper {...styles.availabilityWrapper}>
                                            <ProductLeadTime
                                                showShortLeadTimeDesc={true}
                                                overrideLT={row.leadTime}
                                                displayInfoToolTip={false}
                                                displayLabel={false}
                                                displayAsStatus={false}
                                            />
                                        </StyledWrapper>
                                        {hasMeaningfulMinimumOrderQuantity(row.leadTime?.minimumOrderQuantity) && (
                                            <StyledWrapper {...styles.availabilityWrapper}>
                                                <ProductLineMinimumOrderQuantity
                                                    minimumOrderQuantity={row.leadTime.minimumOrderQuantity}
                                                />
                                            </StyledWrapper>
                                        )}
                                        <StyledWrapper {...styles.availabilityWrapper}>
                                            <ProductFullTruckLoad
                                                overrideFtl={row.fullTruckLoad}
                                                displayTexts={false}
                                                displayInfoTooltip={false}
                                                displaySeparator={false}
                                            />
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell>{renderPricingCell(row)}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
                <Modal
                    {...styles.shipToModal}
                    hideTitle={true}
                    isOpen={changeShipModal}
                    handleClose={() => setChangeShipModal(false)}
                >
                    <Typography {...styles.shipToModalTitle}>
                        {translate("Are you sure you want to change Ship to?")}
                    </Typography>
                    <Typography {...styles.shipToModalText}>{siteMessage("changeShipTo_MLCMessage")}</Typography>
                    <FormActions {...styles.shipToModalFormActions}>
                        <Button
                            {...styles.shipToModalContinueButton}
                            onClick={handleChangeClicked}
                            data-test-selector="changeShipTo_continue"
                        >
                            {translate("Change Ship To")}
                        </Button>
                        <Button
                            {...styles.shipToModalCancelButton}
                            onClick={() => {
                                setChangeShipModal(false);
                            }}
                            data-test-selector="changeShipTo_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                </Modal>
                <Modal
                    {...styles.headerModal}
                    headline={modalContent.heading}
                    isOpen={modalIsOpen}
                    handleClose={() => {
                        setModalIsOpen(false);
                        setModalContent(defaultModalContent);
                    }}
                >
                    <Typography variant="p">{modalContent.content}</Typography>
                </Modal>
            </AccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: PreviouslyPurchasedByTable,
    definition: {
        displayName: "Previously Purchased By Table",
        group: "Product Details",
        icon: "table-layout",
        allowedContexts: [constants.pageType.productDetails],
        fieldDefinitions: [
            {
                name: fields.isMlcDisplay,
                displayName: "Display for MLC Product",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
